import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// In-memory cache so multiple components mounting at the same time
// (HomePage + DynamicPage + ThemeApplier + CookieBar) share a single network
// request instead of each firing their own /api/cms/config/public call.
//
// Strategy:
//   - First caller starts the fetch and is given the in-flight promise.
//   - Any caller arriving while the fetch is still running gets the same
//     promise (no duplicate request).
//   - Once the request resolves we remember the result for `TTL_MS` so that
//     SPA route changes (back/forward navigation) reuse it instead of
//     refetching.
//   - `forceRefresh()` clears the cache so admin saves can pull fresh data.

const TTL_MS = 60 * 1000; // 60 seconds is plenty for SPA navigation

let cachedAt = 0;
let cachedData = null;
let inFlight = null;
let revision = 0;

export const fetchPublicCmsConfig = async () => {
  // Preview override: if URL has ?preview_proposal=<id>, fetch the preview
  // version of the CMS config (injects the proposal page) instead of the
  // live config. This bypasses the cache so previews always reflect the
  // latest proposal data. Also forwards ?theme=<preset|custom|none> for
  // theme switching inside the preview.
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const previewId = params.get('preview_proposal');
      if (previewId) {
        const theme = params.get('theme');
        const qs = theme ? `?theme=${encodeURIComponent(theme)}` : '';
        const resp = await axios.get(`${API}/cms/preview-proposal/${previewId}${qs}`);
        return resp.data;
      }
    } catch {
      // fall through to normal cache
    }
  }
  const now = Date.now();
  if (cachedData && now - cachedAt < TTL_MS) {
    return cachedData;
  }
  if (inFlight) return inFlight;
  const requestedRevision = revision;
  inFlight = axios
    .get(`${API}/cms/config/public`)
    .then((resp) => {
      if (requestedRevision !== revision) return fetchPublicCmsConfig();
      cachedData = resp.data;
      cachedAt = Date.now();
      inFlight = null;
      return cachedData;
    })
    .catch((err) => {
      inFlight = null;
      throw err;
    });
  return inFlight;
};

export const invalidateCmsConfigCache = () => {
  revision += 1;
  cachedData = null;
  cachedAt = 0;
  inFlight = null;
};
if (typeof window !== 'undefined') window.addEventListener('cms-config-updated', invalidateCmsConfigCache);

// Same shared-cache pattern for the legacy /api/content endpoint that powers
// HomePage's `siteConfig`. It is read-only on the public site so a 60s TTL
// is safe.
let contentCachedAt = 0;
let contentCachedData = null;
let contentInFlight = null;

export const fetchPublicContent = async () => {
  const now = Date.now();
  if (contentCachedData && now - contentCachedAt < TTL_MS) {
    return contentCachedData;
  }
  if (contentInFlight) return contentInFlight;
  contentInFlight = axios
    .get(`${API}/content`)
    .then((resp) => {
      contentCachedData = resp.data;
      contentCachedAt = Date.now();
      contentInFlight = null;
      return contentCachedData;
    })
    .catch((err) => {
      contentInFlight = null;
      throw err;
    });
  return contentInFlight;
};

export const invalidatePublicContentCache = () => {
  contentCachedData = null;
  contentCachedAt = 0;
};
